import React, { useState, FC, useCallback } from 'react';
import {
  Dialog,
  Box,
  DialogContent,
  Typography,
  Button,
  useMediaQuery,
  DialogActions,
  Grid,
  IconButton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import type { CustomTheme } from 'types/theme';
import Slider from '@mui/material/Slider';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import getCroppedImg from 'utils/crop';
import { Close } from '@mui/icons-material';

interface EditImageProps {
  open: boolean;
  onClose: () => void;
  onFinish: (file: Blob) => void;
  onDisableLoading: () => void;
  imagem: any;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  container: {
    maxHeight: 700,
    margin: 25,
    '& .MuiPaper-root': {
      borderRadius: 4
    },

    '& .MuiDialog-paperWidthMd': {
      maxWidth: 744
    },

    [theme.breakpoints.down('md')]: {
      '& .MuiDialog-paperWidthMd': {
        maxWidth: 576
      }
    }
  },
  dialogContent: {
    height: 400,
    padding: theme.spacing(8.25, 12.5, 13.75, 12.5),
    background: '#333',
    position: 'relative',

    '@media (max-width:800px)': {
      padding: theme.spacing(5, 7.75, 8.25, 7.75)
    },

    '@media (max-width:450px)': {
      padding: theme.spacing(0, 2),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }
  },
  adjustmentContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    display: 'flex',
    marginTop: 20
  },
  titleBox: {
    paddingLeft: theme.spacing(2.5),
    passingRight: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 50,
    '& h5': {
      lineHeight: 1.25,
      '&::first-letter': {
        textTransform: 'uppercase'
      }
    }
  },
  closeButton: {
    width: 40,
    height: 40,
    textAlign: 'end',
    color: 'rgba(0, 0, 0, 0.54)',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  }
}));

const INITIAL_CROP = { x: 0, y: 0 };
const INITIAL_ZOOM = 1;
const INITIAL_ROTATION = 0;
const ImageEditor: FC<EditImageProps> = ({
  open,
  onClose,
  onFinish,
  onDisableLoading,
  imagem
}) => {
  const classes = useStyles();
  const [crop, setCrop] = useState(INITIAL_CROP);
  const [rotation, setRotation] = useState(INITIAL_ROTATION);
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fullScreen = useMediaQuery('(max-width:450px)');

  const onCropComplete = useCallback((_: any, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels as any);
  }, []);

  const onReset = () => {
    setCrop(INITIAL_CROP);
    setZoom(INITIAL_ZOOM);
    setRotation(INITIAL_ROTATION);
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
  const _onClose = () => {
    onClose();
    onReset();
  };

  const zoomPercent = (value: any) => {
    return `${Math.round(value * 100)}%`;
  };

  const resetDefault = () => {
    setRotation(INITIAL_ROTATION);
    setZoom(INITIAL_ZOOM);
    setCrop(INITIAL_CROP);
  };

  const handleCropImage = async () => {
    if (croppedAreaPixels) {
      const cropImage = await getCroppedImg(
        imagem,
        croppedAreaPixels,
        rotation
      );
      onFinish(cropImage);
      onClose();
      resetDefault();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      open={open}
      onClose={_onClose}
      className={classes.container}
    >
      <Box className={classes.titleBox}>
        <Typography variant="h5">Editar foto do perfil</Typography>
        <IconButton
          id="cancel-avatar-change-id"
          className={classes.closeButton}
          onClick={_onClose}
          size="large"
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent dividers className={classes.dialogContent}>
        <Cropper
          image={imagem}
          crop={crop}
          zoom={zoom}
          restrictPosition={false}
          rotation={rotation}
          aspect={1}
          cropShape="round"
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onRotationChange={setRotation}
          onZoomChange={setZoom}
          onMediaLoaded={onDisableLoading}
          style={{ containerStyle: { margin: 10 } }}
        />
      </DialogContent>
      <DialogActions style={{ flexDirection: 'column' }}>
        <Grid
          container
          item
          spacing={0}
          className={classes.adjustmentContainer}
        >
          <Box style={{ width: '45%' }}>
            <Typography>Zoom: {zoomPercent(zoom)}</Typography>
            <Slider
              valueLabelDisplay="auto"
              min={0.4}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e, zoom) => setZoom(Number(zoom))}
            />
          </Box>
          <Box style={{ width: '45%' }}>
            <Typography>Ajustar: {`${rotation}°`}</Typography>
            <Slider
              valueLabelDisplay="auto"
              min={0}
              max={360}
              value={rotation}
              onChange={(e, rotation) => setRotation(Number(rotation))}
            />
          </Box>
        </Grid>
        <Grid
          container
          item
          spacing={0}
          style={{ marginTop: 10, justifyContent: 'flex-end', display: 'flex' }}
        >
          <Box style={{ padding: '10px 24px 24px 0' }}>
            <Button
              onClick={_onClose}
              id="cancel-avatar-change-id"
              color="primary"
              variant="outlined"
              style={{ marginRight: '15px', maxHeight: 36, border: 0 }}
            >
              CANCELAR
            </Button>

            <Button
              id="save-avatar-change-id"
              color="primary"
              type="submit"
              variant="contained"
              style={{ width: 88, maxHeight: 36 }}
              onClick={handleCropImage}
            >
              SALVAR
            </Button>
          </Box>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default ImageEditor;
